/**
 * Light, dark or match the system: one person's choice of how the app draws.
 *
 * Light is the default for everyone, including anyone with nothing stored.
 * Dark is opt-in, and "Match system" follows the machine's own setting.
 *
 * The choice lives in three places, each for a reason:
 *
 *   - the server (`/api/v1/me/preferences`), so it follows the person to
 *     another browser or the desktop app;
 *   - this browser's localStorage, so a reload draws the right scheme before
 *     the server has answered;
 *   - index.html reads that same key before React loads, so a dark user never
 *     sees a flash of light.
 *
 * The key is new. Existing `hawfinch_*` keys are never renamed: a rename
 * would silently reset everyone's saved state.
 */
import { useEffect } from 'react';
import {
  useMantineColorScheme,
  type MantineColorScheme,
  type MantineColorSchemeManager,
} from '@mantine/core';
import { usePreferences, useSavePreferences, type UserPreferences } from './api/queries';

export type Appearance = UserPreferences['appearance'];

/** Read by the inline script in index.html too; keep the two in step. */
export const APPEARANCE_KEY = 'hawfinch_appearance';

export const APPEARANCE_OPTIONS: { value: Appearance; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'Match system' },
];

export function isAppearance(raw: unknown): raw is Appearance {
  return raw === 'light' || raw === 'dark' || raw === 'system';
}

/** Mantine calls "match the system" `auto`; the server and storage say `system`. */
export function toScheme(a: Appearance): MantineColorScheme {
  return a === 'system' ? 'auto' : a;
}

export function fromScheme(s: MantineColorScheme): Appearance {
  return s === 'auto' ? 'system' : s;
}

function readStored(): Appearance | null {
  try {
    const raw = localStorage.getItem(APPEARANCE_KEY);
    return isAppearance(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Mantine's colour scheme store, kept under our own key and in our own
 * words, so index.html can read it without knowing Mantine's.
 */
export function appearanceManager(): MantineColorSchemeManager {
  let handler: ((e: StorageEvent) => void) | undefined;
  return {
    get: (defaultValue) => {
      const stored = readStored();
      return stored ? toScheme(stored) : defaultValue;
    },
    set: (value) => {
      try {
        localStorage.setItem(APPEARANCE_KEY, fromScheme(value));
      } catch {
        /* private mode: the server copy still applies on the next load */
      }
    },
    // Another tab changing it changes this one too.
    subscribe: (onUpdate) => {
      handler = (e) => {
        if (e.storageArea === localStorage && e.key === APPEARANCE_KEY && isAppearance(e.newValue)) {
          onUpdate(toScheme(e.newValue));
        }
      };
      window.addEventListener('storage', handler);
    },
    unsubscribe: () => {
      if (handler) window.removeEventListener('storage', handler);
    },
    clear: () => {
      try {
        localStorage.removeItem(APPEARANCE_KEY);
      } catch {
        /* nothing to clear */
      }
    },
  };
}

/** The current choice and a setter that saves it for this person. */
export function useAppearance() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const save = useSavePreferences();
  return {
    value: fromScheme(colorScheme),
    set: (next: Appearance) => {
      setColorScheme(toScheme(next));
      save.mutate({ appearance: next });
    },
  };
}

/**
 * Brings this browser into line with the saved choice once the server has
 * answered. Mounted once, inside the signed-in app.
 */
export function useSyncAppearance() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const saved = usePreferences().data?.appearance;
  useEffect(() => {
    if (saved && toScheme(saved) !== colorScheme) setColorScheme(toScheme(saved));
    // Only when the server's answer changes; a local change saves itself.
  }, [saved]); // eslint-disable-line react-hooks/exhaustive-deps
}
